/*
 * „The JA Austria Platform" — zwei Minuten für andere JA-Organisationen in Europa.
 *
 * WOFÜR (01.11.2026): JA Austria zeigt anderen Länderorganisationen, wie das
 * ganze Junior-Jahr auf einer Plattform läuft. Englisch, weil das Publikum
 * aus ganz Europa kommt.
 *
 * DER BOGEN folgt dem Jahr einer Junior Company — von den Stammdaten bis
 * zur Urkunde:
 *   Titel        eine Plattform, die Zahlen des letzten Schuljahrs
 *   Stammdaten   Schulen, Lehrkräfte, Schüler:innen, Bundesland, Kompetenzkatalog
 *   Formen       JC, JCC, JBC — Regeln je Form, zentral geschaltet
 *   Gründung     Geschäftsidee, Markt- und Risikoanalyse, Elternbestätigung
 *   Kurse        Skills4Retail-Konzept (15 Felder) und der KI-Kurs von TalentsLounge
 *   Unterstützer Expert:innen, Jury, Trainer:innen; Bedarfsmeldung, Einladung, Profil
 *   Betrieb      Rechnungen, Erinnerungen, Mitteilungen in der Plattform und per E-Mail
 *   Workflow     Anforderungen, Aufgaben, Freigaben — mit Status und Verlauf
 *   Urkunde      Urkunden am Ende des Jahres
 *   Schluss      Einladung zum Austausch
 *
 * QUELLEN: Zahlen aus dem öffentlichen Jahresbericht (592 Junior Companies,
 * 4.966 Schüler:innen, 185 Schulen, 9 Bundesländer, Schuljahr 2025/26);
 * 33 Kompetenzen aus dem Katalog; 15 Felder à 0–10 Punkte aus dem
 * Skills4Retail-Konzept. Nichts darüber hinaus.
 *
 * Zahlen stehen ausgeschrieben, damit die Stimme sie richtig liest. Wer einen
 * Satz ändert, muss neu vertonen — der Test vergleicht den Text mit
 * `manifest.json`.
 */

export const SHE = 'she'
export const HE = 'he'

/** ElevenLabs-v4-Stimmen über Higgsfield (Presets „Emily" und „John"). */
export const VOICES = {
	[SHE]: '6b3e3642-f7b7-4cb8-9688-51e233c4b92f',
	[HE]: '6b528d43-c056-4a2f-9d82-1591a7ba13b0'
}

export const SLUG = 'jaa-plattform-2026'
export const AUDIO = `/praesentationen-medien/${SLUG}`
export const MUSIC = `${AUDIO}/musik.mp3`
/** Datum im Namen wegen des Browser-Zwischenspeichers. */
export const PDF = `${AUDIO}/ja-austria-platform-2026-11-01.pdf`

export const SLIDES = [
	{ id: 'title', short: 'One platform' },
	{ id: 'masterData', short: 'Master data' },
	{ id: 'forms', short: 'Company forms' },
	{ id: 'founding', short: 'Founding' },
	{ id: 'courses', short: 'Courses' },
	{ id: 'supporters', short: 'Supporters' },
	{ id: 'operations', short: 'Running the year' },
	{ id: 'workflow', short: 'Workflow' },
	{ id: 'certificates', short: 'Certificates' },
	{ id: 'closing', short: "Let's talk" }
]

const a = (id, step, voice, text) => ({ id, step, voice, text })

export const LINES = [
	a('title', 0, HE, 'Junior Achievement Austria runs the entire Junior year on one platform.'),
	a(
		'title',
		1,
		SHE,
		'Last school year: five hundred ninety-two student companies, almost five thousand students, one hundred eighty-five schools - in all nine federal states.'
	),

	a(
		'masterData',
		0,
		HE,
		'It starts with clean master data: schools, teachers, students and their companies - each linked to its federal state.'
	),
	a(
		'masterData',
		1,
		SHE,
		'A shared catalogue of thirty-three competences connects students, teachers and business experts.'
	),

	a(
		'forms',
		0,
		HE,
		'Three company forms run side by side: the Junior Company, the Junior Compact Company and the Junior Basic Company.'
	),
	a(
		'forms',
		1,
		SHE,
		'Each form has its own steps and rules - switched centrally in the settings, not built per school.'
	),

	a(
		'founding',
		0,
		SHE,
		'Students found their company online: the business idea, then a market and risk analysis - every risk with a strategy.'
	),
	a(
		'founding',
		1,
		HE,
		'Parental consent forms are uploaded right there - and only count once the teacher has checked them.'
	),

	a(
		'courses',
		0,
		HE,
		'Learning is built in. In Skills4Retail, students write their business concept in fifteen fields, guided by a learning path with quizzes.'
	),
	a(
		'courses',
		1,
		SHE,
		'Every field is scored from zero to ten. And our AI course from TalentsLounge runs embedded in the platform.'
	),

	a('supporters', 0, SHE, 'Supporters are managed in one place: business experts, jury members and trainers.'),
	a(
		'supporters',
		1,
		HE,
		'JA Austria posts a need per federal state - and supporters reply directly in the platform.'
	),
	a(
		'supporters',
		2,
		SHE,
		'Experts are invited, present their offer in a profile, and students request contact themselves.'
	),

	a(
		'operations',
		0,
		HE,
		'During the year, the platform issues invoices and sends reminders before every report deadline.'
	),
	a(
		'operations',
		1,
		SHE,
		'Messages reach students and teachers inside the platform - and by e-mail, from central templates.'
	),

	a(
		'workflow',
		0,
		HE,
		'Behind it all runs a workflow system: requests, tasks and approvals - each with a status, an owner and a full history.'
	),
	a('workflow', 1, SHE, 'Nothing gets lost. Every change is logged.'),

	a('certificates', 0, SHE, 'At the end of the year, the certificates come straight from the platform.'),
	a('certificates', 1, HE, 'From the first record to the certificate: one system, no spreadsheets.'),

	a('closing', 0, SHE, 'We would love to share what we have learned. Let us talk!')
]

/** Anzahl Schritte einer Folie (= Ansagen dieser Folie). */
export const stepsOf = id => LINES.filter(x => x.id === id).length || 1

/** Dateiname der Aufnahme eines Schritts. */
export const fileOf = line => `${line.id}-${line.step}.mp3`
